/**
 * RevealingTitle — a chat title that changes gracefully.
 *
 * Thin: `useTitleReveal` decides WHAT to show and WHEN, this turns the phase
 * into motion. The element keeps its box throughout (no height change, no
 * reflow), so a title landing under the reader's cursor does not move the row
 * they were about to click.
 *
 * Used by both surfaces that show a chat's name — the sidebar row and the
 * header crumb — so "New Session" becomes the model's title the same way in
 * both, at the same moment.
 */

import React from 'react';
import clsx from 'clsx';
import { IN_MS, OUT_MS, useTitleReveal } from './titleReveal';

interface Props {
  text: string;
  className?: string;
  /** The element to render as — a row uses `p`, a crumb uses `span`. */
  as?: 'p' | 'span' | 'h2' | 'h3';
}

const RevealingTitle: React.FC<Props> = ({ text, className, as: Tag = 'p' }) => {
  const { text: shown, phase } = useTitleReveal(text);

  return (
    <Tag
      data-testid="revealing-title"
      data-phase={phase}
      // The full title stays available to assistive tech and to hover, so a
      // truncated row is never the only copy of it.
      title={text}
      aria-label={text}
      style={{ transitionDuration: `${phase === 'out' ? OUT_MS : IN_MS}ms` }}
      className={clsx(
        className,
        'inline-block transition-[opacity,transform,filter] ease-slide will-change-[opacity,transform]',
        phase === 'out' && 'opacity-0 -translate-y-1 blur-[1px]',
        // The start state of the arrival: low and invisible, held one frame.
        phase === 'enter' && 'opacity-0 translate-y-1 blur-[1px]',
        phase === 'idle' && 'opacity-100 translate-y-0 blur-0'
      )}
    >
      {shown}
    </Tag>
  );
};

export default RevealingTitle;
